import { useState } from "react"
import { router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { CancelReasonDialog } from "@/features/staff/cancel-reason-dialog"
import { formatDateTime } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * The book: who is coming tonight, and to which table.
 *
 * **A table can only be held once, and the database is what says so.** The
 * screen does not check for overlaps and must not: two hosts on two phones both
 * pass any check written here. When the second one loses, the server answers
 * with "esa mesa ya está apartada a esa hora" — which is why the form stays
 * filled on failure rather than clearing, since the host is about to change one
 * field and try again.
 *
 * Times render in the RESTAURANT's zone, never the browser's. A host in Tijuana
 * looking at a book resolved on the server's clock would see tonight's late
 * bookings filed under tomorrow.
 */

export type Reservation = {
  id: number
  guestName: string
  guestPhone: string | null
  partySize: number
  status: string
  statusLabel: string
  startsAt: string
  endsAt: string
  tableId: number | null
  tableName: string | null
  customerId: number | null
  cancelReason: string | null
}

export type TableOption = { value: string; label: string; testId: string }

function tBook(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.reservations.index.${key}`, options)
}

const TONE: Record<string, "reserved" | "occupied" | "available" | "disabled" | "danger"> = {
  booked: "reserved",
  seated: "occupied",
  completed: "available",
  cancelled: "disabled",
  no_show: "danger",
}

/** Only a booking that is still holding a slot can be seated or cancelled. */
const HOLDING = ["booked", "seated"]

function ReservationRow({
  reservation,
  timeZone,
}: {
  reservation: Reservation
  timeZone: string
}) {
  return (
    <li className="space-y-2 px-4 py-3" data-testid={`reservation-${reservation.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium">{reservation.guestName}</span>
          <span className="text-muted-foreground block truncate text-xs tabular-nums">
            {tBook("row_meta", {
              time: formatDateTime(reservation.startsAt, { timeZone }),
              party: reservation.partySize,
            })}
            {reservation.tableName ? ` · ${reservation.tableName}` : ` · ${tBook("no_table")}`}
          </span>
        </span>

        <StatusBadge
          tone={TONE[reservation.status] ?? "disabled"}
          label={reservation.statusLabel}
          data-testid={`reservation-status-${reservation.id}`}
        />
      </div>

      {HOLDING.includes(reservation.status) ? (
        <div className="flex flex-wrap items-center gap-2">
          {reservation.status === "booked" ? (
            <Button
              className="h-11 md:h-9"
              data-testid={`reservation-seat-${reservation.id}`}
              onClick={() =>
                router.patch(`/reservations/${reservation.id}/seat`, {}, { preserveScroll: true })
              }
            >
              {tBook("seat")}
            </Button>
          ) : null}

          <CancelReasonDialog
            path={`/reservations/${reservation.id}/cancel`}
            method="patch"
            payload={(reason) => ({ cancel_reason: reason })}
            reasons={[
              { value: "guest_called", label: tBook("reason_guest_called") },
              { value: "no_show", label: tBook("reason_no_show") },
              { value: "restaurant_cannot_host", label: tBook("reason_cannot_host") },
            ].map((option) => ({
              ...option,
              testId: `reservation-cancel-${reservation.id}-option-${option.value}`,
            }))}
            testIdPrefix={`reservation-cancel-${reservation.id}`}
            triggerTestId={`reservation-cancel-trigger-${reservation.id}`}
            triggerLabel={tBook("cancel")}
            copy={{
              title: tBook("cancel_title"),
              description: tBook("cancel_description"),
              reasonLabel: tBook("cancel_reason_label"),
              reasonPlaceholder: tBook("cancel_reason_placeholder"),
              cancel: tBook("cancel_back"),
              confirm: tBook("cancel_confirm"),
              submitting: tBook("cancel_submitting"),
            }}
            confirmVariant="destructive"
          />
        </div>
      ) : null}
    </li>
  )
}

export function ReservationBook({
  reservations,
  tables,
  timeZone,
  holdingCount,
}: {
  reservations: Reservation[]
  tables: TableOption[]
  timeZone: string
  holdingCount: number
}) {
  const [guestName, setGuestName] = useState("")
  const [partySize, setPartySize] = useState("2")
  const [tableId, setTableId] = useState("")
  const [startsAt, setStartsAt] = useState("")
  const [endsAt, setEndsAt] = useState("")

  return (
    <Card density="compact" data-testid="reservation-book">
      <CardHeader className="border-b">
        <CardTitle>{tBook("title")}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <p
          className="text-foreground text-sm font-semibold tabular-nums"
          data-testid="reservation-holding-count"
        >
          {tBook("holding_count", { count: holdingCount })}
        </p>

        {reservations.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="reservation-empty">
            {tBook("empty")}
          </p>
        ) : (
          <ul className="divide-border divide-y rounded-md border" data-testid="reservation-list">
            {reservations.map((reservation) => (
              <ReservationRow key={reservation.id} reservation={reservation} timeZone={timeZone} />
            ))}
          </ul>
        )}

        {/* An explicit submit: a name, a time and a table only mean anything
            together, and the server may still refuse the slot. */}
        <form
          className="flex flex-wrap items-end gap-2"
          data-testid="reservation-create"
          onSubmit={(event) => {
            event.preventDefault()
            router.post(
              "/reservations",
              {
                guest_name: guestName,
                party_size: partySize,
                location_table_id: tableId,
                starts_at: startsAt,
                ends_at: endsAt,
              },
              { preserveScroll: true }
            )
          }}
        >
          <label className="flex min-w-40 flex-1 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tBook("guest_label")}</span>
            <input
              className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
              value={guestName}
              onChange={(event) => setGuestName(event.target.value)}
              data-testid="reservation-guest-input"
            />
          </label>

          <label className="flex w-20 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tBook("party_label")}</span>
            <input
              className="border-input h-11 rounded-md border px-3 text-sm tabular-nums md:h-9"
              inputMode="numeric"
              value={partySize}
              onChange={(event) => setPartySize(event.target.value)}
              data-testid="reservation-party-input"
            />
          </label>

          <label className="flex w-40 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tBook("table_label")}</span>
            <select
              className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
              value={tableId}
              onChange={(event) => setTableId(event.target.value)}
              data-testid="reservation-table-select"
            >
              {/* A booking with no table is legitimate: the host takes the call
                  first and decides where it sits later. */}
              <option value="">{tBook("no_table")}</option>
              {tables.map((table) => (
                <option key={table.value} value={table.value} data-testid={table.testId}>
                  {table.label}
                </option>
              ))}
            </select>
          </label>

          <label className="flex w-48 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tBook("starts_label")}</span>
            <input
              type="datetime-local"
              className="border-input h-11 rounded-md border px-3 text-sm tabular-nums md:h-9"
              value={startsAt}
              onChange={(event) => setStartsAt(event.target.value)}
              data-testid="reservation-starts-input"
            />
          </label>

          <label className="flex w-48 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tBook("ends_label")}</span>
            <input
              type="datetime-local"
              className="border-input h-11 rounded-md border px-3 text-sm tabular-nums md:h-9"
              value={endsAt}
              onChange={(event) => setEndsAt(event.target.value)}
              data-testid="reservation-ends-input"
            />
          </label>

          <Button
            type="submit"
            className="h-11 md:h-9"
            disabled={guestName.trim() === "" || startsAt === "" || endsAt === ""}
            data-testid="reservation-submit"
          >
            {tBook("add")}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
